import React, { useState, useEffect } from 'react';
import {
  Shield,
  Gavel,
  Droplet,
  Archive,
  ChevronDown,
  ChevronUp,
  MessageSquare,
  Mail,
  Phone,
  Bot,
  ArrowRight,
  BookOpen,
  Trees,
  Scissors,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Wrench,
} from 'lucide-react';

interface EducationModuleProps {
  initialTab?: 'retailer' | 'awareness' | 'warranty';
}

type Tab = 'retailer' | 'awareness' | 'warranty';

const TABS: { id: Tab; label: string }[] = [
  { id: 'retailer', label: 'Retailer support' },
  { id: 'awareness', label: 'Machete awareness' },
  { id: 'warranty', label: 'Warranty & claims' },
];

const FAQS = [
  {
    q: 'Is it legal to sell machetes to minors?',
    a: 'We strictly enforce an 18+ policy for all retail sales. While agricultural tools are exempt from certain weapon prohibitions, selling to minors creates significant liability. Always request ID.',
  },
  {
    q: 'Why did the blade rust so quickly?',
    a: 'SAE 1075 high carbon steel is not stainless. It is designed for hardness and edge retention. Surface rust is normal if not oiled. We recommend a light coat of 3-in-1 oil, mineral oil, or WD-40 after every use.',
  },
  {
    q: 'A customer chipped the blade on a log. Is this a warranty issue?',
    a: 'No. Machetes are velocity tools designed for green vegetation, not mass tools like axes which are for dead wood. Striking hard knots or seasoned logs will damage the fine edge. This is considered improper use.',
  },
  {
    q: "How do I explain the 'black residue' on the blade?",
    a: 'That is the firescale from the heat-treatment process. It is left on intentionally to provide a layer of rustic corrosion resistance on the flats of the blade. It is a mark of authenticity.',
  },
];

const EducationModule: React.FC<EducationModuleProps> = ({ initialTab = 'retailer' }) => {
  const [activeTab, setActiveTab] = useState<Tab>(initialTab);
  const [activeFaq, setActiveFaq] = useState<number | null>(null);

  useEffect(() => {
    setActiveTab(initialTab);
  }, [initialTab]);

  const toggleFaq = (index: number) => {
    setActiveFaq(activeFaq === index ? null : index);
  };

  return (
    <div className="bg-paper min-h-screen">

      {/* Header */}
      <header className="relative bg-forge text-paper py-24 lg:py-28 px-6 lg:px-10">
        <div className="max-w-editorial mx-auto">
          <p className="font-mono text-[11px] tracking-micro uppercase text-ember-soft mb-6">
            — Knowledge base
          </p>
          <h1 className="font-editorial italic font-light text-5xl lg:text-7xl text-paper-warm leading-[1.02] tracking-tight mb-10 max-w-prose">
            Resource center.
          </h1>

          {/* Tabs */}
          <div className="flex flex-col md:flex-row gap-0 md:gap-2 max-w-3xl border-t border-paper/15 pt-8">
            {TABS.map((tab) => {
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`relative flex-1 py-3 text-sm transition-colors duration-150 ${
                    isActive ? 'text-paper-warm' : 'text-paper/55 hover:text-paper'
                  }`}
                >
                  <span className="font-mono text-[10px] tracking-micro uppercase mr-3 text-paper/40">
                    0{TABS.indexOf(tab) + 1}
                  </span>
                  {tab.label}
                  {isActive && (
                    <span className="absolute left-0 -top-[33px] h-px w-full bg-ember" aria-hidden="true" />
                  )}
                </button>
              );
            })}
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-brass/40 to-transparent" />
      </header>

      {/* RETAILER */}
      {activeTab === 'retailer' && (
        <div className="max-w-editorial mx-auto px-6 lg:px-10 py-20 lg:py-28 animate-in fade-in duration-500 ease-editorial">

          {/* Field safety protocols */}
          <section className="mb-24">
            <div className="flex items-center gap-3 mb-10 pb-6 border-b border-ink-200">
              <Shield className="w-4 h-4 text-ember" strokeWidth={1.5} />
              <p className="font-mono text-[11px] tracking-micro uppercase text-ember">
                — Field safety protocols
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-10">
              {[
                { n: '01', title: 'The blood circle', body: '360-degree clearance is mandatory. If you can touch someone with the tip, they are too close.' },
                { n: '02', title: 'Handle-first handover', body: 'Never hand a blade point-first. Grip the spine, present the handle, and maintain contact until "Thank you".' },
                { n: '03', title: 'Safe follow-through', body: "Avoid the 'Triangle of Death' (inner thighs). Always swing away from your body's silhouette." },
                { n: '04', title: 'The pinch grip', body: 'Control comes from the thumb and index finger, not a white-knuckle squeeze. Reduces fatigue.' },
              ].map((item) => (
                <article key={item.n} className="border-l border-ink-200 pl-6 group hover:border-ember transition-colors duration-300 ease-editorial">
                  <p className="font-editorial italic text-4xl text-ink-300 group-hover:text-ember transition-colors duration-300 ease-editorial mb-4">
                    {item.n}
                  </p>
                  <h3 className="font-editorial italic text-xl text-ink-950 mb-3 tracking-tight">
                    {item.title}.
                  </h3>
                  <p className="text-sm text-ink-700 leading-relaxed">{item.body}</p>
                </article>
              ))}
            </div>
          </section>

          {/* Care & Maintenance */}
          <section className="mb-24">
            <div className="flex items-center gap-3 mb-10 pb-6 border-b border-ink-200">
              <Wrench className="w-4 h-4 text-ember" strokeWidth={1.5} />
              <p className="font-mono text-[11px] tracking-micro uppercase text-ember">
                — Care & maintenance
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Sharpening */}
              <div className="bg-paper-bright border border-ink-200 p-8">
                <div className="w-10 h-10 bg-paper border border-ink-200 flex items-center justify-center mb-5">
                  <svg className="w-4 h-4 text-ink-700" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M14 4l8 8-2 2-8-8 2-2z" />
                    <path d="M12 6L4 14l-2 6 6-2 8-8" />
                  </svg>
                </div>
                <h3 className="font-editorial italic text-xl text-ink-950 mb-3 tracking-tight">Sharpening protocol.</h3>
                <p className="text-sm text-ink-700 leading-relaxed mb-5">
                  Do not use bench grinders — they heat the steel and ruin the temper. For optimal performance, use only the{' '}
                  <span className="text-ink-950 font-medium">Machete Man Mill File</span> to maintain the convex edge geometry.
                </p>
                <div className="bg-paper border border-ember/30 p-3">
                  <p className="font-mono text-[10px] tracking-micro uppercase text-ember mb-1">
                    Recommended tool
                  </p>
                  <p className="font-editorial italic text-base text-ink-950">MM Mill File (10")</p>
                  <p className="font-mono text-[10px] tracking-wider text-ink-500 mt-1">SKU · MM-O-MILLFILE</p>
                </div>
              </div>

              {/* Oiling */}
              <div className="bg-paper-bright border border-ink-200 p-8">
                <div className="w-10 h-10 bg-paper border border-ink-200 flex items-center justify-center mb-5">
                  <Droplet className="w-4 h-4 text-ember" strokeWidth={1.5} />
                </div>
                <h3 className="font-editorial italic text-xl text-ink-950 mb-3 tracking-tight">Oiling & rust proofing.</h3>
                <p className="text-sm text-ink-700 leading-relaxed mb-5">
                  High carbon steel will rust. This is a trade-off for edge retention. Instruct customers to wipe clean and apply a thin film of oil (mineral, 3-in-1, or gun oil) before storing.
                </p>
                <p className="font-mono text-[10px] tracking-micro uppercase text-ink-500">— Essential step</p>
              </div>

              {/* Storage */}
              <div className="bg-paper-bright border border-ink-200 p-8">
                <div className="w-10 h-10 bg-paper border border-ink-200 flex items-center justify-center mb-5">
                  <Archive className="w-4 h-4 text-ink-700" strokeWidth={1.5} />
                </div>
                <h3 className="font-editorial italic text-xl text-ink-950 mb-3 tracking-tight">Long-term storage.</h3>
                <p className="text-sm text-ink-700 leading-relaxed mb-5">
                  Do not store in a wet leather sheath for long periods — leather absorbs moisture. Store in a cool, dry place. If storing in a sheath, wrap the blade in oiled paper first.
                </p>
                <button className="font-mono text-[10px] tracking-micro uppercase text-ember hover:text-ember-deep transition-colors duration-150">
                  Download guide →
                </button>
              </div>
            </div>
          </section>

          {/* Legal & Liability */}
          <section className="bg-forge text-paper p-10 lg:p-16 mb-24 relative overflow-hidden">
            <div className="absolute top-0 right-0 opacity-[0.04] pointer-events-none">
              <Gavel className="w-72 h-72 text-paper" strokeWidth={0.5} />
            </div>
            <div className="relative z-10">
              <p className="font-mono text-[11px] tracking-micro uppercase text-ember-soft mb-6">
                — Liability & compliance
              </p>
              <h2 className="font-editorial italic font-light text-4xl lg:text-5xl text-paper-warm leading-[1.1] tracking-tight mb-12">
                Tools, not weapons.
              </h2>

              <div className="grid md:grid-cols-2 gap-12">
                <div>
                  <p className="font-mono text-[10px] tracking-micro uppercase text-brass mb-4">— Legal classification</p>
                  <p className="text-paper/75 leading-[1.65] mb-6">
                    "Is this legal to carry?" Our tools are classified as{' '}
                    <span className="text-paper-warm font-medium">Agricultural Implements</span> under Canadian law. They are tools, not weapons. However, context matters — carrying one in a downtown mall is illegal; carrying one in a truck for camping is legitimate utility.
                  </p>
                  <button className="inline-flex items-center gap-2 border border-paper/30 hover:border-paper hover:bg-paper/5 text-paper px-5 py-2.5 text-sm font-medium transition-colors duration-150">
                    Download legal brief
                  </button>
                </div>
                <div>
                  <p className="font-mono text-[10px] tracking-micro uppercase text-brass mb-4">— Liability mitigation</p>
                  <p className="text-paper/75 leading-[1.65] mb-6">
                    Retailers must establish clear "Swing Zones" in store if allowing customers to handle blades. We provide printable waivers and signage to ensure your shop remains safe and compliant.
                  </p>
                  <button className="inline-flex items-center gap-2 border border-paper/30 hover:border-paper hover:bg-paper/5 text-paper px-5 py-2.5 text-sm font-medium transition-colors duration-150">
                    Get store signage
                  </button>
                </div>
              </div>
            </div>
          </section>

          {/* Q&A */}
          <section className="max-w-prose mx-auto">
            <div className="mb-12 text-center">
              <p className="font-mono text-[11px] tracking-micro uppercase text-ember mb-4">— Retailer Q&A</p>
              <h2 className="font-editorial italic font-light text-4xl text-ink-950 leading-tight tracking-tight mb-3">
                At the sales counter.
              </h2>
              <p className="text-ink-700">Common questions encountered in store.</p>
            </div>

            <div className="space-y-3">
              {FAQS.map((faq, index) => {
                const isOpen = activeFaq === index;
                return (
                  <div key={index} className="border border-ink-200 bg-paper-bright">
                    <button
                      onClick={() => toggleFaq(index)}
                      className="w-full flex items-center justify-between gap-4 p-5 hover:bg-paper transition-colors duration-150 text-left"
                    >
                      <span className="font-editorial italic text-lg text-ink-950 leading-snug">
                        {faq.q}
                      </span>
                      {isOpen ? (
                        <ChevronUp className="w-4 h-4 text-ember flex-shrink-0" strokeWidth={1.5} />
                      ) : (
                        <ChevronDown className="w-4 h-4 text-ink-500 flex-shrink-0" strokeWidth={1.5} />
                      )}
                    </button>
                    {isOpen && (
                      <div className="p-5 pt-1 border-t border-ink-200 bg-paper">
                        <p className="text-ink-700 text-sm leading-[1.65]">{faq.a}</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        </div>
      )}

      {/* AWARENESS */}
      {activeTab === 'awareness' && (
        <div className="max-w-editorial mx-auto px-6 lg:px-10 py-20 lg:py-28 animate-in fade-in duration-500 ease-editorial">

          <div className="mb-20 max-w-prose">
            <p className="font-mono text-[11px] tracking-micro uppercase text-ember mb-5">— Consumer education</p>
            <h2 className="font-editorial italic font-light text-4xl lg:text-5xl text-ink-950 leading-[1.05] tracking-tight mb-6">
              A machete is the most versatile tool on earth.
            </h2>
            <p className="text-ink-700 text-lg leading-[1.65]">
              It requires respect. We provide these guides to help you educate your customers on proper technique, selection, and care.
            </p>
          </div>

          <div className="space-y-20">
            {/* Topic 1: Velocity vs. Mass */}
            <article className="grid grid-cols-1 md:grid-cols-12 gap-10 lg:gap-16 pb-16 border-b border-ink-200">
              <aside className="md:col-span-4">
                <div className="bg-paper-bright border border-ink-200 p-7">
                  <AlertTriangle className="w-6 h-6 text-ember mb-5" strokeWidth={1.5} />
                  <p className="font-mono text-[10px] tracking-micro uppercase text-ember mb-3">— Topic 01</p>
                  <h3 className="font-editorial italic text-2xl text-ink-950 leading-tight tracking-tight mb-2">
                    Velocity vs. mass.
                  </h3>
                  <p className="text-sm text-ink-700">The core physics of the tool.</p>
                </div>
              </aside>
              <div className="md:col-span-8">
                <h4 className="font-editorial italic text-2xl text-ink-950 mb-5 tracking-tight">Understanding the mechanics.</h4>
                <p className="text-ink-700 text-base leading-[1.65] mb-6">
                  The most common mistake new users make is treating a machete like a hatchet. An axe or hatchet relies on <span className="text-ink-950 font-medium">mass</span> (head weight) to crush and split wood fibers. A machete relies on <span className="text-ink-950 font-medium">velocity</span> (tip speed) to slice through vegetation.
                </p>
                <ul className="space-y-4 border-l border-ink-200 pl-6">
                  <li className="text-sm text-ink-900 leading-relaxed">
                    <span className="font-editorial italic text-ink-950">The wrist snap.</span> Power comes from a relaxed shoulder and a sharp snap of the wrist at the point of impact — not a stiff-armed bludgeon.
                  </li>
                  <li className="text-sm text-ink-900 leading-relaxed">
                    <span className="font-editorial italic text-ink-950">The 45° rule.</span> Never strike perpendicular to the target. Always cut at a 45-degree angle to slice effectively and prevent the blade from bouncing back.
                  </li>
                  <li className="text-sm text-ink-900 leading-relaxed">
                    <span className="font-editorial italic text-ink-950">Glancing blows.</span> Velocity tools can deflect easily. Maintain a firm pinch grip to prevent the blade from twisting in the hand upon impact.
                  </li>
                </ul>
              </div>
            </article>

            {/* Topic 2: Garden */}
            <article className="grid grid-cols-1 md:grid-cols-12 gap-10 lg:gap-16 pb-16 border-b border-ink-200">
              <aside className="md:col-span-4">
                <div className="bg-paper-bright border border-ink-200 p-7">
                  <Scissors className="w-6 h-6 text-olive mb-5" strokeWidth={1.5} />
                  <p className="font-mono text-[10px] tracking-micro uppercase text-olive mb-3">— Topic 02</p>
                  <h3 className="font-editorial italic text-2xl text-ink-950 leading-tight tracking-tight mb-2">
                    Garden & landscape.
                  </h3>
                  <p className="text-sm text-ink-700">Replacing shears and string trimmers.</p>
                </div>
              </aside>
              <div className="md:col-span-8">
                <h4 className="font-editorial italic text-2xl text-ink-950 mb-5 tracking-tight">The tropical solution to backyard jungles.</h4>
                <p className="text-ink-700 text-base leading-[1.65] mb-8">
                  In Canada, we over-complicate gardening with specialized gadgets. A 14" to 18" Latin machete is often more efficient for general maintenance.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  {[
                    { label: 'Composting', body: 'Rapidly chop garden waste into fine mulch directly on the pile. Faster than feeding a shredder.' },
                    { label: 'Edging', body: 'Use the tip of a straight blade to cut clean, deep edges along flower beds or walkways.' },
                    { label: 'Vine management', body: 'The only tool capable of reaching high climbing vines and severing them cleanly without jamming.' },
                    { label: 'Root pruning', body: 'Machetes are tough enough to strike into the soil to sever invasive roots (unlike delicate pruning saws).' },
                  ].map((item) => (
                    <div key={item.label} className="border-l border-ink-200 pl-4">
                      <p className="font-mono text-[10px] tracking-micro uppercase text-ember mb-1.5">
                        {item.label}
                      </p>
                      <p className="text-sm text-ink-700 leading-relaxed">{item.body}</p>
                    </div>
                  ))}
                </div>
              </div>
            </article>

            {/* Topic 3: Bushcraft */}
            <article className="grid grid-cols-1 md:grid-cols-12 gap-10 lg:gap-16 pb-16 border-b border-ink-200">
              <aside className="md:col-span-4">
                <div className="bg-paper-bright border border-ink-200 p-7">
                  <Trees className="w-6 h-6 text-brass mb-5" strokeWidth={1.5} />
                  <p className="font-mono text-[10px] tracking-micro uppercase text-brass mb-3">— Topic 03</p>
                  <h3 className="font-editorial italic text-2xl text-ink-950 leading-tight tracking-tight mb-2">
                    Camping & bushcraft.
                  </h3>
                  <p className="text-sm text-ink-700">Shelter building and firewood.</p>
                </div>
              </aside>
              <div className="md:col-span-8">
                <h4 className="font-editorial italic text-2xl text-ink-950 mb-5 tracking-tight">The one-tool option.</h4>
                <p className="text-ink-700 text-base leading-[1.65] mb-6">
                  For the camper, weight is the enemy. A machete (specifically the Bolo or Panga profile) bridges the gap between a knife and an axe, handling 90% of camp tasks.
                </p>
                <ul className="space-y-4 border-l border-ink-200 pl-6">
                  <li className="text-sm text-ink-900 leading-relaxed flex gap-3">
                    <BookOpen className="w-4 h-4 text-ember flex-shrink-0 mt-1" strokeWidth={1.5} />
                    <span>
                      <span className="font-editorial italic text-ink-950">Batoning wood.</span> A full-tang machete is excellent for splitting kindling. Place the blade on the log and strike the spine with a wooden baton.
                    </span>
                  </li>
                  <li className="text-sm text-ink-900 leading-relaxed flex gap-3">
                    <BookOpen className="w-4 h-4 text-ember flex-shrink-0 mt-1" strokeWidth={1.5} />
                    <span>
                      <span className="font-editorial italic text-ink-950">Clearing tent pads.</span> Use the flat of the blade to sweep debris, and the edge to level saplings flush with the ground so they don't puncture the tent floor.
                    </span>
                  </li>
                  <li className="text-sm text-ink-900 leading-relaxed flex gap-3">
                    <BookOpen className="w-4 h-4 text-ember flex-shrink-0 mt-1" strokeWidth={1.5} />
                    <span>
                      <span className="font-editorial italic text-ink-950">The hardwood warning.</span> Do not chop into seasoned (dry) hardwood knots with the thin section of the blade. This creates chips. Use a saw or axe for seasoned oak or maple.
                    </span>
                  </li>
                </ul>
              </div>
            </article>

            {/* Topic 4: Rust */}
            <article className="grid grid-cols-1 md:grid-cols-12 gap-10 lg:gap-16">
              <aside className="md:col-span-4">
                <div className="bg-paper-bright border border-ink-200 p-7">
                  <Droplet className="w-6 h-6 text-steel mb-5" strokeWidth={1.5} />
                  <p className="font-mono text-[10px] tracking-micro uppercase text-steel mb-3">— Topic 04</p>
                  <h3 className="font-editorial italic text-2xl text-ink-950 leading-tight tracking-tight mb-2">
                    Rust prevention.
                  </h3>
                  <p className="text-sm text-ink-700">Living with high carbon steel.</p>
                </div>
              </aside>
              <div className="md:col-span-8">
                <h4 className="font-editorial italic text-2xl text-ink-950 mb-5 tracking-tight">The cost of performance.</h4>
                <p className="text-ink-700 text-base leading-[1.65] mb-6">
                  We use SAE 1075 high carbon steel. Unlike stainless steel (which is soft and loses its edge), carbon steel is hard and resilient. However, it reacts to oxygen and moisture.
                </p>

                <div className="bg-paper-bright border border-ink-200 p-6">
                  <p className="font-mono text-[10px] tracking-micro uppercase text-ember mb-4">— The 3-step care protocol</p>
                  <ol className="space-y-3">
                    <li className="text-sm text-ink-900 leading-relaxed flex gap-3">
                      <span className="font-mono text-ink-500 flex-shrink-0">01</span>
                      <span>
                        <span className="font-editorial italic text-ink-950">Clean.</span> Remove all sap and plant matter immediately after use. Sap is acidic and eats steel.
                      </span>
                    </li>
                    <li className="text-sm text-ink-900 leading-relaxed flex gap-3">
                      <span className="font-mono text-ink-500 flex-shrink-0">02</span>
                      <span>
                        <span className="font-editorial italic text-ink-950">Dry.</span> Wipe completely dry.
                      </span>
                    </li>
                    <li className="text-sm text-ink-900 leading-relaxed flex gap-3">
                      <span className="font-mono text-ink-500 flex-shrink-0">03</span>
                      <span>
                        <span className="font-editorial italic text-ink-950">Oil.</span> Apply a thin film of oil. 3-in-1, mineral oil, or even cooking spray works for short-term storage.
                      </span>
                    </li>
                  </ol>
                </div>

                <p className="text-ink-700 text-sm italic mt-5 leading-relaxed">
                  <span className="text-ink-950 font-medium not-italic">Note on patina —</span> Over time, the blade will turn grey or black. This is a forced oxidation called a "patina" and actually helps protect against destructive red rust. Do not scrub it off.
                </p>
              </div>
            </article>
          </div>
        </div>
      )}

      {/* WARRANTY */}
      {activeTab === 'warranty' && (
        <div className="max-w-editorial mx-auto px-6 lg:px-10 py-20 lg:py-28 animate-in fade-in duration-500 ease-editorial">

          <div className="max-w-prose mx-auto text-center mb-20 pb-12 border-b border-ink-200">
            <p className="font-mono text-[11px] tracking-micro uppercase text-ember mb-5">— Carbon steel covenant</p>
            <h2 className="font-editorial italic font-light text-4xl lg:text-5xl text-ink-950 leading-[1.1] tracking-tight mb-6">
              We stand behind every blade.
            </h2>
            <p className="text-ink-700 text-lg leading-[1.65]">
              Because these are "consumable tools" built for heavy agricultural use, our B2B warranty is strictly defined to separate{' '}
              <span className="text-ink-950 font-medium">manufacturing defects</span> from{' '}
              <span className="text-ink-950 font-medium">field abuse</span>.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {/* Covered */}
            <div className="bg-paper-bright border border-ink-200 p-8 lg:p-10">
              <div className="flex items-center gap-3 mb-8 pb-6 border-b border-ink-200">
                <CheckCircle className="w-5 h-5 text-olive" strokeWidth={1.5} />
                <p className="font-mono text-[11px] tracking-micro uppercase text-olive">— Covered claims</p>
              </div>
              <ul className="space-y-7">
                {[
                  { tag: 'Lifetime', title: 'Heat treatment failure', body: 'If a blade snaps cleanly in half during normal vegetation clearing, this indicates a grain structure failure. We will replace these immediately.' },
                  { tag: '1 year', title: 'Handle separation', body: 'If rivets loosen or wood scales detach under normal use. Does not cover handles damaged by "overstriking" or missing the target.' },
                  { tag: '30 days', title: 'Shipping damage', body: 'Bent blades or crushed cases upon arrival. Must be reported within 48 hours of delivery with photos of packaging.' },
                ].map((item) => (
                  <li key={item.title}>
                    <span className="font-mono text-[10px] tracking-micro uppercase text-olive border border-olive/30 px-2 py-0.5 inline-block mb-3">
                      {item.tag}
                    </span>
                    <h4 className="font-editorial italic text-lg text-ink-950 mb-2 tracking-tight">{item.title}.</h4>
                    <p className="text-sm text-ink-700 leading-relaxed">{item.body}</p>
                  </li>
                ))}
              </ul>
            </div>

            {/* Not Covered */}
            <div className="bg-paper-bright border border-ink-200 p-8 lg:p-10">
              <div className="flex items-center gap-3 mb-8 pb-6 border-b border-ink-200">
                <XCircle className="w-5 h-5 text-ember" strokeWidth={1.5} />
                <p className="font-mono text-[11px] tracking-micro uppercase text-ember">— Not covered · user error</p>
              </div>
              <ul className="space-y-7">
                {[
                  { tag: 'Chemistry', title: 'Oxidation & rust', body: 'Rust is chemically inevitable on high carbon steel if not oiled. This is a maintenance issue, not a defect. We do not accept returns for rusty blades.' },
                  { tag: 'Physics', title: 'Edge chipping & rolling', body: 'Caused by striking rocks, concrete, or dry hardwood knots. The edge is designed for green vegetation. Hard impacts will chip the fine edge.' },
                  { tag: 'Abuse', title: 'Batoning damage', body: 'While our blades are tough, hammering the spine with a rock or metal pipe (instead of wood) to split logs voids the warranty.' },
                ].map((item) => (
                  <li key={item.title}>
                    <span className="font-mono text-[10px] tracking-micro uppercase text-ember border border-ember/30 px-2 py-0.5 inline-block mb-3">
                      {item.tag}
                    </span>
                    <h4 className="font-editorial italic text-lg text-ink-950 mb-2 tracking-tight">{item.title}.</h4>
                    <p className="text-sm text-ink-700 leading-relaxed">{item.body}</p>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Partner Help Desk */}
      <section className="bg-forge text-paper py-24 lg:py-32 relative">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-brass/40 to-transparent" />
        <div className="max-w-editorial mx-auto px-6 lg:px-10">

          <div className="mb-16 max-w-prose">
            <p className="font-mono text-[11px] tracking-micro uppercase text-ember-soft mb-5">— Partner help desk</p>
            <h2 className="font-editorial italic font-light text-4xl lg:text-5xl text-paper-warm leading-[1.05] tracking-tight mb-4">
              Direct support.
            </h2>
            <p className="text-paper/70 text-lg">For authorized dealers.</p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 lg:gap-8">

            {/* AI Agent */}
            <article className="bg-forge-deep border border-paper/10 p-8 lg:p-10 relative overflow-hidden group">
              <div className="absolute top-0 right-0 opacity-[0.04] group-hover:opacity-[0.08] transition-opacity duration-500 ease-editorial pointer-events-none">
                <Bot className="w-56 h-56 text-paper" strokeWidth={0.5} />
              </div>
              <div className="relative z-10">
                <div className="inline-flex items-center gap-2 mb-6">
                  <span className="w-1.5 h-1.5 bg-olive rounded-full animate-pulse" />
                  <span className="font-mono text-[10px] tracking-micro uppercase text-ember-soft">
                    System online · 24/7
                  </span>
                </div>
                <h3 className="font-editorial italic text-2xl text-paper-warm mb-4 tracking-tight">
                  Technical support agent.
                </h3>
                <p className="text-paper/65 leading-[1.65] text-sm mb-8">
                  Our AI specialist is trained on SAE 1075 metallurgy, Canadian import law, and warranty protocols. Use the chat widget in the bottom-right corner for instant answers.
                </p>
                <div className="inline-flex items-center gap-2 text-ember-soft text-sm font-medium">
                  <MessageSquare className="w-4 h-4" strokeWidth={1.5} />
                  Access via widget
                  <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />
                </div>
              </div>
            </article>

            {/* Human */}
            <article className="bg-paper-bright text-ink-950 p-8 lg:p-10">
              <h3 className="font-editorial italic text-2xl text-ink-950 mb-8 tracking-tight inline-flex items-center gap-3">
                <Phone className="w-5 h-5 text-ember" strokeWidth={1.5} />
                Direct wholesale line.
              </h3>

              <div className="space-y-7">
                <div className="flex items-start gap-4">
                  <Mail className="w-4 h-4 text-ember mt-1.5 flex-shrink-0" strokeWidth={1.5} />
                  <div>
                    <p className="font-mono text-[10px] tracking-micro uppercase text-ink-500 mb-1">
                      Commercial inquiries
                    </p>
                    <a
                      href="mailto:info@themacheteman.com"
                      className="font-editorial italic text-xl text-ink-950 hover:text-ember transition-colors duration-150"
                    >
                      info@themacheteman.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <Phone className="w-4 h-4 text-ember mt-1.5 flex-shrink-0" strokeWidth={1.5} />
                  <div>
                    <p className="font-mono text-[10px] tracking-micro uppercase text-ink-500 mb-1">Phone</p>
                    <a
                      href="tel:+14166840010"
                      className="font-editorial italic text-xl text-ink-950 hover:text-ember transition-colors duration-150"
                    >
                      (416) 684-0010
                    </a>
                    <p className="font-mono text-[10px] tracking-wider text-ink-500 mt-1.5">
                      Mon–Fri · 9 AM – 5 PM EST · Also: (813) 455-0411
                    </p>
                  </div>
                </div>
              </div>
            </article>
          </div>
        </div>
      </section>
    </div>
  );
};

export default EducationModule;
